import { container } from 'tsyringe'
import Action from '@/common/action'
import {
  BODY_LABEL_ADDON_ANIMATION,
  BODY_LABEL_ADDON_FROM_SLIT,
  BODY_LABEL_ADDON_REVEALED,
  BODY_LABEL_EPHEMERAL_TOOL,
  MATTER_SCALE,
  PhysicsLayerZ
} from '@/features/experience/misc/constants'
import { lerp } from '@/shared/misc/math-helpers'
import { Bodies, Body, Bounds, Constraint, Engine, Events, IEventCollision, Vector, World } from 'matter-js'
import { MathUtils, PerspectiveCamera, Vector2, Vector3 } from 'three'
import SceneUtils from '../misc/scene-utils'
import RoomPhysics, { CollisionCategory, CollisionGroup } from './room-physics-base'
import { Interface } from '@/shared/di/interface'
import type { ITouch } from '@/shared/di/interface/touch'
import type { ISound } from '@/shared/di/interface/sound'
import { IDebug } from '@/shared/di/interface/debug'
import PhysicsUtils from '../misc/physics-utils'
import { ToolVariant } from '@/state/vault/domain'

const DROP_SPEED_THRESHOLD = 0.1
const MAKE_ROOM_SPEED_THRESHOLD = 1
const MAKE_ROOM_DISTANCE_THRESHOLD = 170
const PINCH_DISTANCE_THRESHOLD = 170

const tempVector2 = new Vector2()
const bodyPositionBackLayer = new Vector3()
const bodyPositionFrontLayer = new Vector3()

const sound = container.resolve<ISound>(Interface.ISound)

interface TouchConstraint {
  touchid: number
  hasUpdated: boolean
  body: Body
  target: Vector2
  rotateConstraintPivotBody: Body
  rotateConstraint: Constraint
  lastKnownInputPosition: Vector2
  constraintOrigin?: TouchConstraint
  controllerdByOtherConstraint?: TouchConstraint
  hidden: boolean
  startDistance?: number
}

export enum SlitType {
  TRANSFER = 'transfer',
  SAFE = 'safe',
  WALLET = 'wallet',
  DIVIDE = 'divide',
  COMBINE = 'combine',
  EXCHANGE = 'exchange'
}

export interface CoinInteractionEvent {
  id: number
}

export interface CoinOverSlitEvent {
  coinId: number
  slot: string
}

export default class RoomPhysicsInteractions {
  #camera: PerspectiveCamera = new PerspectiveCamera()
  #physics: RoomPhysics
  #connectionReady = false
  #tubeEnabled = false
  #activeUserIsAvailable = false
  #touchConstraints: TouchConstraint[] = []
  #outsideTouches: Touch[] = []
  #blockTouchIdMap: Map<number, boolean> = new Map()
  #dropWaitingList: Body[] = []
  #debounceSoundMap: Map<string, number> = new Map<string, number>()
  #lastColliderCollision = 0
  #collisionSoundVolume = 0

  #firstTouchSpeed = 0

  #nextTouchesList: Touch[] = []
  #touchesDirty = false

  onTokenEnteredSlit = new Action<CoinOverSlitEvent>()
  onTokenLifted = new Action<CoinInteractionEvent>()
  onTokenTapped = new Action<CoinInteractionEvent>()
  onTokenDropped = new Action<CoinInteractionEvent>()
  onTokenPinched = new Action<CoinInteractionEvent>()
  onTokenEnteredTube = new Action<any>()
  onCoinPulledIntoSlit: Action<SlitType> = new Action<SlitType>()

  constructor(physics: RoomPhysics) {
    this.#physics = physics
  }

  setCamera(camera: PerspectiveCamera) {
    this.#camera = camera
  }

  setConnectionReady(value: boolean) {
    this.#connectionReady = value
  }

  setActiveUserAvailable(value: boolean) {
    this.#activeUserIsAvailable = value
  }

  enableTube() {
    this.#tubeEnabled = true
  }

  disableTube() {
    this.#tubeEnabled = false
  }

  getActiveTouchPoints() {
    return this.#outsideTouches
  }

  activate() {
    sound.startLoop('drag_1')
    sound.startLoop('drag_2')
    sound.startLoop('dragcollide_coin')
    sound.setLoopVolume('drag_1', 0)
    sound.setLoopVolume('drag_2', 0)
    sound.setLoopVolume('dragcollide_coin', 0)

    this.onTouchesChanged([])
    container.resolve<ITouch>(Interface.ITouch).onTouchesChanged.add(this.onTouchesChanged)
    //container.resolve<ITouch>(Interface.ITouch).onLongPress.add(this.onLongPress)

    Events.on(this.#physics.engine, 'collisionStart', this.handleCollisionStart)
    Events.on(this.#physics.engine, 'collisionActive', this.handleCollisionActive)
  }

  inactivate() {
    this.clearAllTouchConstraints()

    container.resolve<ITouch>(Interface.ITouch).onTouchesChanged.remove(this.onTouchesChanged)
    //container.resolve<ITouch>(Interface.ITouch).onLongPress.remove(this.onLongPress)

    sound.stopLoop('drag_1')
    sound.stopLoop('drag_2')
    sound.stopLoop('dragcollide_coin')

    Events.off(this.#physics.engine, 'collisionStart', this.handleCollisionStart)
    Events.off(this.#physics.engine, 'collisionActive', this.handleCollisionActive)
  }

  clearAllTouchConstraints() {
    for (let index = this.#touchConstraints.length - 1; index >= 0; index--) {
      const cacheItem = this.#touchConstraints[index]
      World.remove(this.#physics.engine.world, cacheItem.rotateConstraintPivotBody)
      World.remove(this.#physics.engine.world, cacheItem.rotateConstraint)
      if (!cacheItem.hidden) {
        this.addToDropWaitingList(cacheItem.body)
      } else {
        World.remove(this.#physics.engine.world, cacheItem.body)
      }
    }
    this.#touchConstraints.length = 0
  }

  /*
  private onLongPress = () => {
    for (let index = this.#touchConstraints.length - 1; index >= 0; index--) {
      const cacheItem = this.#touchConstraints[0]
      if (cacheItem.hidden) {
        World.remove(this.#physics.engine.world, cacheItem.body)
      }
    }
  }*/

  private onTouchesChanged = (touches: Touch[]) => {
    //reset camera offset

    this.#nextTouchesList = touches
    this.#touchesDirty = true
  }

  private processIncomingTouches() {
    this.#touchesDirty = false

    if (this.#nextTouchesList.length === 0) {
      this.#blockTouchIdMap.clear()
      this.#debounceSoundMap.clear()
    }

    //create new or update existing

    this.#nextTouchesList.forEach((touch, index) => {
      const touchItem = this.#touchConstraints.find(item => item.touchid === touch.identifier)

      tempVector2.set(touch.clientX, touch.clientY)

      if (this.#blockTouchIdMap.get(touch.identifier)) {
        //return
      }

      if (touchItem === undefined) {
        //test if pinch first. Cancel if ongoing pinch already
        if (
          index > 0 &&
          SceneUtils.isCoin(this.#touchConstraints[0].body) &&
          this.#touchConstraints[index - 1] &&
          !this.#touchConstraints[index - 1].constraintOrigin &&
          !this.#touchConstraints.find(tc => tc.controllerdByOtherConstraint)
        ) {
          const touchItemOrigin = this.#nextTouchesList[index - 1]
          const touchItemPinch = this.#nextTouchesList[index]
          const constraintOrigin = this.#touchConstraints[index - 1]

          const distanceToPrevious = Math.hypot(
            touchItemOrigin.clientX - touchItemPinch.clientX,
            touchItemOrigin.clientY - touchItemPinch.clientY
          )

          if (distanceToPrevious < PINCH_DISTANCE_THRESHOLD) {
            this.createPinchConstraint(touchItemOrigin, touchItemPinch, constraintOrigin, distanceToPrevious)
            return
          }
        }

        //create new touch item
        bodyPositionBackLayer.copy(SceneUtils.screenToSceneSpace(this.#camera, tempVector2, PhysicsLayerZ.Back))
        bodyPositionBackLayer.multiplyScalar(MATTER_SCALE)

        const waitingToDropControllers = this.#physics.bodies.filter(body => this.#dropWaitingList.includes(body))

        //hit waitingToDropControllers coins in upper layer
        let bodyHit = SceneUtils.raycastBodies(this.#camera, waitingToDropControllers, touch, PhysicsLayerZ.Front)
        if (bodyHit) {
          this.createNewTouchItem(touch, bodyHit)
        } else {
          const activeBodies = this.#physics.bodies.filter(body => !this.#dropWaitingList.includes(body))

          //hit ui layer?
          bodyHit = SceneUtils.raycastBodies(
            this.#camera,
            activeBodies.filter(b => b.label.includes(BODY_LABEL_EPHEMERAL_TOOL)),
            touch,
            PhysicsLayerZ.UI
          )
          if (bodyHit) {
            this.createNewTouchItem(touch, bodyHit)
          } else {
            //hit back layer?
            bodyHit = SceneUtils.raycastBodies(this.#camera, activeBodies, touch, PhysicsLayerZ.Back)
            if (bodyHit) {
              this.createNewTouchItem(touch, bodyHit)
            } else {
              //hit front layer?
              bodyHit = SceneUtils.raycastBodies(this.#camera, activeBodies, touch, PhysicsLayerZ.Front)
              if (bodyHit) {
                this.createNewTouchItem(touch, bodyHit)
              } else {
                //no hit, so create hidden constraint
                this.createNewHiddenTouchItem(touch)
                this.#blockTouchIdMap.set(touch.identifier, true)
              }
            }
          }
        }
      } else {
        bodyPositionFrontLayer.copy(
          SceneUtils.screenToSceneSpace(
            this.#camera,
            tempVector2,
            touchItem.hidden ? PhysicsLayerZ.Back : PhysicsLayerZ.Front
          )
        )
        bodyPositionFrontLayer.multiplyScalar(MATTER_SCALE)
        touchItem.target.x = bodyPositionFrontLayer.x
        touchItem.target.y = bodyPositionFrontLayer.y

        touchItem.hasUpdated = true
        touchItem.lastKnownInputPosition.set(touch.clientX, touch.clientY)
      }
    })

    this.#outsideTouches = this.#nextTouchesList.filter(touch => {
      return !this.#touchConstraints.find(item => item.touchid === touch.identifier && !item.hidden)
    })

    //remove inactive
    for (let index = this.#touchConstraints.length - 1; index >= 0; index--) {
      const tc = this.#touchConstraints[index]

      if (
        !tc.hasUpdated ||
        tc.body.label.includes(BODY_LABEL_ADDON_ANIMATION) ||
        tc.body.label.includes(BODY_LABEL_ADDON_REVEALED) ||
        !this.#nextTouchesList.find(touch => touch.identifier === tc.touchid)
      ) {
        if (!tc.hidden) {
          this.testReleasedOnSlit(tc)
        }
        this.onTokenTapped.invoke({ id: tc.body.id })

        this.removeConstraint(tc)
      }
    }

    this.#touchConstraints.forEach(x => {
      x.hasUpdated = false
    })
  }

  private removeConstraint(rc: TouchConstraint) {
    rc.body.plugin.pinchDistance = 0

    World.remove(this.#physics.engine.world, rc.rotateConstraintPivotBody)
    World.remove(this.#physics.engine.world, rc.rotateConstraint)

    this.#touchConstraints.splice(this.#touchConstraints.indexOf(rc), 1)

    if (rc.hidden) {
      World.remove(this.#physics.engine.world, rc.body)
    } else {
      if (!rc.constraintOrigin) {
        rc.body.collisionFilter.mask = CollisionCategory.StaticWallsFront
        this.addToDropWaitingList(rc.body)
      }
    }

    //remove link if pinch contraint
    if (rc.constraintOrigin) {
      rc.constraintOrigin.controllerdByOtherConstraint = undefined
    }

    //in case removing a touch on a currently pinched coin
    if (rc.controllerdByOtherConstraint) {
      this.removeConstraint(rc.controllerdByOtherConstraint)
    }
  }

  private createPinchConstraint(
    touchItemOrigin: Touch,
    touchItemPinch: Touch,
    constraintOrigin: TouchConstraint,
    startDistance: number
  ) {
    tempVector2.set(touchItemPinch.clientX, touchItemPinch.clientY)
    bodyPositionFrontLayer.copy(SceneUtils.screenToSceneSpace(this.#camera, tempVector2, PhysicsLayerZ.Back))
    bodyPositionFrontLayer.multiplyScalar(MATTER_SCALE)

    const rotateConstraintPivotBody = Bodies.circle(bodyPositionFrontLayer.x, bodyPositionFrontLayer.x, 10, {
      isStatic: false,
      render: {
        visible: true,
        fillStyle: 'red'
      },
      collisionFilter: {
        // don't collide with anything
        group: CollisionCategory.None,
        category: CollisionCategory.SlitBlockers
      }
    })
    const angle = constraintOrigin.body.angle
    const { body } = constraintOrigin
    Body.setAngle(body, 0)
    const rotateConstraint = Constraint.create({
      bodyA: body,
      pointA: { x: 0, y: -130 },
      bodyB: rotateConstraintPivotBody,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 0.001,
      damping: 0.003,
      render: {
        visible: true
      }
    })

    World.add(this.#physics.engine.world, rotateConstraintPivotBody)
    World.add(this.#physics.engine.world, rotateConstraint)
    Body.setAngle(body, angle)

    const newTouchConstraint = {
      touchid: touchItemPinch.identifier,
      body: body,
      target: new Vector2(bodyPositionFrontLayer.x, bodyPositionFrontLayer.y),
      hasUpdated: true,
      rotateConstraint,
      rotateConstraintPivotBody,
      lastKnownInputPosition: new Vector2(),
      constraintOrigin: constraintOrigin,
      startDistance: startDistance,
      hidden: false
    }

    constraintOrigin.controllerdByOtherConstraint = newTouchConstraint
    this.#touchConstraints.push(newTouchConstraint)
  }

  private testReleasedOnSlit(cacheItem: TouchConstraint) {
    if (!this.#connectionReady) {
      return
    }
    tempVector2.copy(cacheItem.lastKnownInputPosition)
    bodyPositionFrontLayer.copy(SceneUtils.screenToSceneSpace(this.#camera, tempVector2, PhysicsLayerZ.Back))
    bodyPositionFrontLayer.multiplyScalar(MATTER_SCALE)

    const testBody = Bodies.circle(bodyPositionFrontLayer.x, bodyPositionFrontLayer.y, 1)
    World.add(this.#physics.engine.world, [testBody])

    const slitBody = this.getSensorUnderBody(testBody)
    if (
      slitBody !== undefined &&
      slitBody.isSensor &&
      !testBody.label.includes(BODY_LABEL_ADDON_ANIMATION) &&
      !testBody.label.includes(BODY_LABEL_ADDON_REVEALED)
    ) {
      this.playEnterSlitSound()
      this.startPullingBodyIntoSlit(slitBody, cacheItem.body).then(() => {
        this.bodyPulledIntoSlit(slitBody, cacheItem.body)
      })
    }

    World.remove(this.#physics.engine.world, testBody)
  }

  bodyPulledIntoSlit(slitBody: Body, pulledBody: Body) {
    const debug = container.resolve<IDebug>(Interface.IDebug)

    const slot = slitBody.label.split('_')[1]

    if (slot === 'top' && this.#physics.getRoomConfig().hasContacts) {
      //always up from a room that can show contacts
      this.playEnterTubeSound()
      this.onTokenEnteredTube.invoke({ id: pulledBody.id })
    } else {
      //always into combine, exchange or down in wallet room. Or any direction in other room
      this.onTokenEnteredSlit.invoke({
        slot: slot,
        coinId: pulledBody.id
      })
    }
  }

  playEnterSlitSound() {
    const sound = container.resolve<ISound>(Interface.ISound)
    sound.play('slit_enter')?.rate(0.9 + Math.random() * 0.2)
  }

  playEnterTubeSound() {
    const sound = container.resolve<ISound>(Interface.ISound)
    //sound.play('tube_enter_' + Math.ceil(Math.random() * 3))?.rate(0.9 + Math.random() * 0.2)
    sound.play('tube_enter')?.rate(0.9 + Math.random() * 0.2)
  }

  private createNewHiddenTouchItem(touch: Touch) {
    const bodyHit = Bodies.circle(bodyPositionBackLayer.x, bodyPositionBackLayer.y, 100, {
      frictionAir: 0.09,
      restitution: 0,
      friction: 0.3,

      collisionFilter: {
        category: CollisionCategory.Coins,
        mask: CollisionCategory.Coins
      }, ////only collide with BACK colliders (2) and coins with group 1
      render: {
        lineWidth: 3,
        fillStyle: 'red'
      }
    })
    bodyHit.plugin.rotateConstraintX = -40

    World.add(this.#physics.engine.world, [bodyHit])

    PhysicsUtils.animateInBody(bodyHit)

    const rotateConstraintPivotBody = Bodies.circle(bodyPositionBackLayer.x, bodyPositionBackLayer.y, 20, {
      isStatic: true,
      render: {
        visible: true
      },
      collisionFilter: {
        // don't collide with anything
        group: -1,
        category: CollisionCategory.None
      }
    })
    const angle = bodyHit.angle

    Body.setAngle(bodyHit, 0)
    const rotateConstraint = Constraint.create({
      bodyA: bodyHit,
      pointA: { x: 0, y: -130 },
      bodyB: rotateConstraintPivotBody,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 0.001,
      damping: 0.003,
      render: {
        visible: true
      }
    })

    World.add(this.#physics.engine.world, rotateConstraintPivotBody)
    World.add(this.#physics.engine.world, rotateConstraint)

    this.#touchConstraints.push({
      touchid: touch.identifier,
      body: bodyHit,
      target: new Vector2(bodyPositionBackLayer.x, bodyPositionBackLayer.y),
      hasUpdated: true,
      rotateConstraint,
      rotateConstraintPivotBody,
      lastKnownInputPosition: new Vector2(),
      hidden: true
    })
  }

  private createNewTouchItem(touch: Touch, bodyHit: Body) {
    if (bodyHit.label.includes(BODY_LABEL_ADDON_ANIMATION)) {
      return
    }

    bodyHit.label = bodyHit.label.split(BODY_LABEL_ADDON_FROM_SLIT)[0]

    this.removeFromDropWaitingList(bodyHit)

    //replace with positions in front layer
    bodyPositionFrontLayer.copy(SceneUtils.screenToSceneSpace(this.#camera, tempVector2, PhysicsLayerZ.Front))
    bodyPositionFrontLayer.multiplyScalar(MATTER_SCALE)

    //if (!bodyHit.label.includes(BODY_LABEL_EPHEMERAL_TOOL)) {
    //don't collide with other coins in back layer, only front layer
    bodyHit.collisionFilter.group = CollisionGroup.ActiveCoins
    //but collide with collision boxes. Collider blocker category removed so it can enter slits
    bodyHit.collisionFilter.mask = CollisionCategory.StaticWallsFront //only collide with FRONT colliders
    //}

    Body.setPosition(bodyHit, { x: bodyPositionFrontLayer.x, y: bodyPositionFrontLayer.y })
    Body.setVelocity(bodyHit, { x: 0, y: 0 })

    /*
      const size = container.resolve<ISize>(Token.ISize).getSize()
      if (bodyHit.position.x < size.width * -0.47) {
        Body.applyForce(bodyHit, bodyHit.position, Vector.create(30, 0))
      } else if (bodyHit.position.x > size.width * 0.47) {
        Body.applyForce(bodyHit, bodyHit.position, Vector.create(-30, 0))
      }
    */

    const rotateConstraintPivotBody = Bodies.circle(bodyPositionFrontLayer.x, bodyPositionFrontLayer.x, 20, {
      isStatic: true,
      render: {
        visible: true
      },
      collisionFilter: {
        // don't collide with anything
        group: -1,
        category: CollisionCategory.None
      }
    })
    const angle = bodyHit.angle

    Body.setAngle(bodyHit, 0)
    const rotateConstraint = Constraint.create({
      bodyA: bodyHit,
      pointA: { x: 0, y: -130 },
      bodyB: rotateConstraintPivotBody,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 0.001,
      damping: 0.003,
      render: {
        visible: true
      }
    })

    World.add(this.#physics.engine.world, rotateConstraintPivotBody)
    World.add(this.#physics.engine.world, rotateConstraint)
    Body.setAngle(bodyHit, angle)

    this.#touchConstraints.push({
      touchid: touch.identifier,
      body: bodyHit,
      target: new Vector2(bodyPositionFrontLayer.x, bodyPositionFrontLayer.y),
      hasUpdated: true,
      rotateConstraint,
      rotateConstraintPivotBody,
      lastKnownInputPosition: new Vector2(),
      hidden: false
    })

    this.onTokenLifted.invoke({ id: bodyHit.id })
  }

  public removeFromDropWaitingList(body: Body) {
    const testIndex = this.#dropWaitingList.indexOf(body)
    if (testIndex > -1) {
      this.#dropWaitingList.splice(testIndex, 1)
    }
  }

  private startPullingBodyIntoSlit(slitBody: Body, body: Body) {
    body.label += BODY_LABEL_ADDON_ANIMATION

    return new Promise<void>(resolve => {
      const forceCenter = new Vector2().copy(slitBody.position as Vector2)
      let invokeDelay = 500

      if (slitBody.label.includes(SlitType.DIVIDE)) {
        forceCenter.x -= 200
        this.onCoinPulledIntoSlit.invoke(SlitType.DIVIDE)
      } else if (slitBody.label.includes(SlitType.COMBINE)) {
        forceCenter.x += 200
        this.onCoinPulledIntoSlit.invoke(SlitType.COMBINE)
        invokeDelay = 800
      }

      if (slitBody.label.includes('top') || slitBody.label.includes('bottom')) {
        if (slitBody.position.y > -this.#physics.getRoomConfig().roomHeight * MATTER_SCALE * 0.5) {
          forceCenter.y += 300
        } else {
          forceCenter.y -= 300
        }
      }

      const newBody = Bodies.circle(forceCenter.x, forceCenter.y, 20, {
        isStatic: true,
        render: {
          visible: true
        },
        collisionFilter: {
          // don't collide with anything
          group: -1,
          category: CollisionCategory.None
        }
      })

      const newContraint = Constraint.create({
        bodyA: body,
        bodyB: newBody,
        length: 0,
        stiffness: 0.001,
        damping: 0.003,
        render: {
          visible: true
        }
      })

      World.add(this.#physics.engine.world, newBody)
      World.add(this.#physics.engine.world, newContraint)

      //TODO: Better async of removing contraint, checking the length of the constraint instead
      window.setTimeout(() => {
        body.collisionFilter.group = CollisionGroup.NoCoins
        body.collisionFilter.mask = CollisionCategory.None

        World.remove(this.#physics.engine.world, newBody)
        World.remove(this.#physics.engine.world, newContraint)

        resolve()
      }, invokeDelay)
    })
  }

  private addForcesToOverlapBodies(touchItem: TouchConstraint) {
    this.#physics.bodies
      //get bodies that is dragged
      .filter(
        (body: Body) =>
          !body.label.includes(BODY_LABEL_ADDON_FROM_SLIT) &&
          this.#touchConstraints &&
          !this.#touchConstraints.find(item => item.body === body)
      )
      .forEach((otherBody: Body) => {
        const a = otherBody.position.x - touchItem.body.position.x
        const b = otherBody.position.y - touchItem.body.position.y
        const dist = Math.abs(Math.sqrt(a * a + b * b))

        if (dist < 170) {
          const deltaVector = Vector.sub(otherBody.position, touchItem.body.position)
          const force = dist * 0.000002

          if (force > 0) {
            const forceVector = Vector.mult(deltaVector, force)

            if (Vector.dot(forceVector, touchItem.body.velocity) > 0) {
              Body.applyForce(otherBody, otherBody.position, forceVector)
            }
          }
        }
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  update(time: number, delta: number) {
    this.#touchConstraints.forEach(touchItem => {
      touchItem.body.plugin.pinchDistance = 0

      //update pinch contraint
      if (touchItem.constraintOrigin && this.#physics.getRoomConfig().canZoomCoin) {
        Body.setPosition(touchItem.rotateConstraintPivotBody, {
          x: lerp(touchItem.rotateConstraintPivotBody.position.x, touchItem.target.x, 0.7, delta),
          y: lerp(touchItem.rotateConstraintPivotBody.position.y, touchItem.target.y, 0.7, delta)
        })

        let pinchDistance = Math.hypot(
          touchItem.constraintOrigin.target.x - touchItem.target.x,
          touchItem.constraintOrigin.target.y - touchItem.target.y
        )

        if (touchItem.startDistance) {
          pinchDistance -= touchItem.startDistance
        }
        if (pinchDistance > 600) {
          this.onTokenPinched.invoke({ id: touchItem.body.id })
        } else {
          touchItem.constraintOrigin.body.plugin.pinchDistance = pinchDistance
          this.addPinchForce(touchItem, touchItem.constraintOrigin)
        }
      } else {
        if (!touchItem.hidden) {
          this.addForcesToOverlapBodies(touchItem)
        }

        this.addForceToDraggedCoin(touchItem)

        const targetX = touchItem.body.position.x + touchItem.body.plugin.rotateConstraintX
        const targetY = touchItem.body.position.y - 240
        Body.setPosition(touchItem.rotateConstraintPivotBody, {
          x: lerp(touchItem.rotateConstraintPivotBody.position.x, targetX, 0.7, delta),
          y: lerp(touchItem.rotateConstraintPivotBody.position.y, targetY, 0.7, delta)
        })
      }
    })
  }

  earlyUpdate(time: number, delta: number) {
    if (this.#touchesDirty) {
      this.processIncomingTouches()
    }
  }

  lateUpdate(time: number, delta: number) {
    this.updateDragSounds()

    //look for previously dragged coins that still have speed
    for (let index = this.#dropWaitingList.length - 1; index >= 0; index--) {
      const body = this.#dropWaitingList[index]

      //skip testing if body is controlled by animation
      if (body.label.includes(BODY_LABEL_ADDON_ANIMATION)) {
        this.removeFromDropWaitingList(body)
      } else {
        //extra preventing fake test tings to mess things up

        if (!body.label.includes(BODY_LABEL_ADDON_REVEALED) && !body.label.toLowerCase().includes('fake')) {
          const collidedWithSensor = this.testCollisionWithSensors(body)

          if (collidedWithSensor) {
            continue
          }
        }

        //check again if coin is animated because of slit activation
        if (body.label.includes(BODY_LABEL_ADDON_ANIMATION)) {
          continue
        }

        if (body.speed < MAKE_ROOM_SPEED_THRESHOLD && body.label !== ToolVariant.Savegoal) {
          this.addForceToMakeRoom(body)
        }

        if (body.speed < DROP_SPEED_THRESHOLD) {
          body.label = body.label.replace(BODY_LABEL_ADDON_REVEALED, '')

          this.onTokenDropped.invoke({ id: body.id })
          this.removeFromDropWaitingList(body)

          Body.setVelocity(body, { x: 0, y: 0 })

          body.label = body.label.replace(BODY_LABEL_ADDON_FROM_SLIT, '')

          //collide with coins and colliders
          if (body.label.includes(BODY_LABEL_EPHEMERAL_TOOL)) {
            body.collisionFilter.group = CollisionGroup.UILayer
            body.collisionFilter.mask = CollisionCategory.UILayer
          } else {
            //collide with coins and colliders
            body.collisionFilter.group = CollisionGroup.InactiveCoins
            //only collide with BACK colliders and coins with group 1
            body.collisionFilter.mask = CollisionCategory.Coins | CollisionCategory.StaticWallsBack
          }
        }
      }
    }
  }

  private updateDragSounds() {
    const sound = container.resolve<ISound>(Interface.ISound)

    if (this.draggedCoins.length > 0) {
      this.#firstTouchSpeed += (Math.min(1, this.draggedCoins[0].speed / 12) - this.#firstTouchSpeed) * 0.2

      sound.setLoopVolume('drag_1', this.#firstTouchSpeed)
      sound.setLoopVolume('drag_2', Math.pow(this.#firstTouchSpeed, 2))
    } else {
      const loop1 = sound.getLoop('drag_1')
      if (loop1) {
        sound.setLoopVolume('drag_1', loop1.volume() * 0.9)
      }

      const loop2 = sound.getLoop('drag_2')
      if (loop2) {
        sound.setLoopVolume('drag_2', loop2.volume() * 0.9)
      }
    }

    if (Date.now() - this.#lastColliderCollision > 100) {
      this.#collisionSoundVolume += (0 - this.#collisionSoundVolume) * 0.1
    }

    sound.setLoopVolume('dragcollide_coin', this.#collisionSoundVolume)
  }

  addForceToDraggedCoin(touchItem: TouchConstraint) {
    Body.setVelocity(touchItem.body, { x: 0, y: 0 })

    const force = new Vector2()
      .copy(touchItem.body.position as Vector2)
      .sub(touchItem.target)
      .multiplyScalar(0.04)
      .negate()

    if (!touchItem.body.label.includes(BODY_LABEL_ADDON_ANIMATION)) {
      Body.applyForce(touchItem.body, touchItem.body.position, force)
    }
  }

  private addPinchForce(firstTouchItem: TouchConstraint, constraintOrigin: TouchConstraint) {
    const force = new Vector2().copy(firstTouchItem.target).sub(constraintOrigin.target).multiplyScalar(0.02)
    Body.applyForce(firstTouchItem.body, firstTouchItem.body.position, force)
  }

  private testCollisionWithSensors(body: Body): boolean {
    if (
      body.plugin.pinchDistance > 0 ||
      body.label.includes(BODY_LABEL_ADDON_ANIMATION) ||
      body.label.includes(BODY_LABEL_ADDON_REVEALED) ||
      body.label.includes(BODY_LABEL_ADDON_FROM_SLIT)
    ) {
      return false
    }

    let bCollided = false

    this.#physics.slitSensors.forEach(slitBody => {
      //test collision
      if (Bounds.overlaps(body.bounds, slitBody.bounds) && slitBody.isSensor && this.#connectionReady) {
        bCollided = true
        this.startPullingBodyIntoSlit(slitBody, body).then(() => {
          this.bodyPulledIntoSlit(slitBody, body)
        })
      }
    })

    return bCollided
  }

  private getSensorUnderBody(testBody: Body): Body | undefined {
    let collidesWith: Body | undefined = undefined
    this.#physics.slitSensors.forEach(slitBody => {
      //test collision
      if (Bounds.overlaps(testBody.bounds, slitBody.bounds)) {
        collidesWith = slitBody
      }
    })

    return collidesWith
  }

  addForceToMakeRoom(body: Body) {
    this.#physics.bodies
      .filter(body => !body.label.includes(BODY_LABEL_ADDON_FROM_SLIT))
      .forEach(otherBody => {
        const a = otherBody.position.x - body.position.x
        const b = otherBody.position.y - body.position.y
        const dist = Math.abs(Math.sqrt(a * a + b * b))

        if (dist < MAKE_ROOM_DISTANCE_THRESHOLD) {
          const deltaVector = Vector.sub(otherBody.position, body.position)
          const force = dist * 0.00001

          if (force > 0) {
            const forceVector = Vector.mult(deltaVector, force)
            Body.applyForce(otherBody, otherBody.position, forceVector)
          }
        }
      })
  }

  addToDropWaitingList(body: Body) {
    if (!body.label.includes(BODY_LABEL_ADDON_ANIMATION)) {
      this.#dropWaitingList.push(body)
    }
  }

  private handleCollisionStart = (event: IEventCollision<Engine>) => {
    const now = Date.now()
    event.pairs.forEach(pair => {
      if (
        SceneUtils.isCoin(pair.bodyA) &&
        SceneUtils.isCoin(pair.bodyB) &&
        pair.bodyA.collisionFilter.mask === CollisionCategory.StaticWallsFront &&
        pair.bodyB.collisionFilter.mask === CollisionCategory.StaticWallsFront
      ) {
        //if two dragged coins or in waitinglist to drop
        //check if one has a touch-contraint, then prevent other to enetr slits
        const testA = this.#touchConstraints.find(tc => tc.body.id === pair.bodyA.id)
        const testB = this.#touchConstraints.find(tc => tc.body.id === pair.bodyB.id)
        if (testA && !testB) {
          pair.bodyB.collisionFilter.mask = CollisionCategory.StaticWallsFront | CollisionCategory.SlitBlockers
        } else if (testB && !testA) {
          pair.bodyA.collisionFilter.mask = CollisionCategory.StaticWallsFront | CollisionCategory.SlitBlockers
        }
      }

      ;[pair.bodyA].forEach(body => {
        if (SceneUtils.isCoin(body) || body.collisionFilter.category === CollisionCategory.StaticWallsFront) {
          const denomination = Number(body.label.substring(5, body.label.length))

          if (isNaN(denomination)) {
            return
          }

          let collideCoinType = '1'
          let maxAlternatives = 1
          if (denomination <= 1000) {
            collideCoinType = '1'
            maxAlternatives = 4
          } else if (denomination < 2000) {
            collideCoinType = '10'
            maxAlternatives = 4
          } else if (denomination < 10000) {
            collideCoinType = '20'
            maxAlternatives = 5
          } else {
            collideCoinType = '100'
            maxAlternatives = 4
          }

          const sound = container.resolve<ISound>(Interface.ISound)
          const rndSnd = Math.ceil(Math.random() * maxAlternatives)
          if (this.#debounceSoundMap.has(collideCoinType)) {
            /*if (now - this.#debounceSoundMap.get(collideCoinType)! > 300) {
              this.#debounceSoundMap.set(collideCoinType, now)
              sound.play(`collide_coin${collideCoinType}_${rndSnd}`)
            }*/
          } else {
            sound.play(`collide_coin${collideCoinType}_${rndSnd}`)
            this.#debounceSoundMap.set(collideCoinType, now)
          }
        }
      })
    })
  }

  private handleCollisionActive = (event: IEventCollision<Engine>) => {
    const now = Date.now()

    event.pairs.forEach(pair => {
      if (
        (SceneUtils.isCoin(pair.bodyA) && SceneUtils.isCollider(pair.bodyB)) ||
        (SceneUtils.isCoin(pair.bodyB) && SceneUtils.isCollider(pair.bodyA))
      ) {
        const isCoinDragged = this.#touchConstraints.find(x => {
          return x.body.label === pair.bodyA.label || x.body.label === pair.bodyB.label
        })
        if (isCoinDragged) {
          this.#lastColliderCollision = now
          let vol = Math.max(Vector.magnitude(pair.bodyA.velocity), Vector.magnitude(pair.bodyB.velocity)) / 60
          vol = MathUtils.clamp(vol, 0, 1)

          this.#collisionSoundVolume += (vol - this.#collisionSoundVolume) * 0.1
        }
      }
    })
  }

  destroy() {
    this.onCoinPulledIntoSlit.removeAll()
    this.onTokenEnteredSlit.removeAll()
    this.onTokenLifted.removeAll()
    this.onTokenDropped.removeAll()
    this.onTokenPinched.removeAll()
    this.onTokenEnteredTube.removeAll()
  }

  get draggedCoins() {
    return this.#touchConstraints.filter(t => !t.hidden).map(t => t.body)
  }

  get isDragging() {
    return this.#touchConstraints.filter(t => !t.hidden).length > 0
  }
}
